import {
  HighwayArea,
  HighwayPoiSpec,
  IdGenerator,
  ManualArea,
  RackAlongsideExtension,
  RackingArea,
  Rectangle,
  Segment,
  SingularStationArea,
  StationaryArea,
  RackSpec,
  RackInsideExtension,
} from '@thive/fddm-types';
import { resolveRackingConnectNeighbors } from '@/modules/connections/factories/connection';

import {
  getHighwayPoiSpecDefault,
  getRackSpecDefault,
  ID_GENERATOR_DEFAULT,
  LOAD_HANDLING_POI_SPEC_DEFAULT,
  POI_SPEC_DEFAULT,
} from '../constants/areas';
import { Area } from '../types';
import { assertNever } from '../../../helpers/utils';
import { AtLeast1, minItemsArrayUtils } from '../helpers/minItems';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';

interface StationaryAreaCreationParameters {
  id: string;
  name: string;
  rectangle: Rectangle;
  supportedVehicleId: string;
  stationaryType: StationaryArea['stationaryType'];
}

interface RackingAreaCreationParameters {
  id: string;
  name: string;
  rectangle: Rectangle;
  supportedVehicleId: string;
  supportedLoadCarrierId: string;
  rackingLoadHandlingType?: RackingArea['rackingLoadHandlingType'];
}

interface SingularStationAreaParameters {
  id: string;
  name: string;
  rectangle: Rectangle;
  supportedVehicleId: string;
  supportedLoadCarrierId: string;
  loadHandlingType: SingularStationArea['loadHandlingType'];
  loadCarrierDimensions: { width: number; length: number };
}

interface ManualAreaParameters {
  id: string;
  name: string;
  rectangle: Rectangle;
  supportedVehicleId: string;
  supportedLoadCarrierId: string;
  loadCarrierDimensions: { width: number; length: number };
}
interface HighwayAreaParameters {
  id: string;
  name: string;
  supportedVehicleId: string;
  segments: AtLeast1<Segment>;
}

type buildAreaParams =
  | {
      areaType: Extract<Area['areaType'], 'STATIONARY'>;
      creationParams: StationaryAreaCreationParameters;
    }
  | {
      areaType: Extract<Area['areaType'], 'RACKING'>;
      creationParams: RackingAreaCreationParameters;
    }
  | {
      areaType: Extract<Area['areaType'], 'SINGULAR'>;
      creationParams: SingularStationAreaParameters;
    }
  | {
      areaType: Extract<Area['areaType'], 'HIGHWAY'>;
      creationParams: HighwayAreaParameters;
    }
  | {
      areaType: Extract<Area['areaType'], 'MANUAL'>;
      creationParams: ManualAreaParameters;
    };

interface IFactory {
  buildArea(
    buildAreaParams: buildAreaParams,
  ): StationaryArea | RackingArea | SingularStationArea | HighwayArea | ManualArea;
  copyArea<T extends Area>(area: T, overrides: Partial<T>): T;
}

class Factory implements IFactory {
  /* Overloads to get type safety */
  buildArea(params: {
    areaType: 'STATIONARY';
    creationParams: StationaryAreaCreationParameters;
  }): StationaryArea;
  buildArea(params: {
    areaType: 'RACKING';
    creationParams: RackingAreaCreationParameters;
  }): RackingArea;
  buildArea(params: {
    areaType: 'SINGULAR';
    creationParams: SingularStationAreaParameters;
  }): SingularStationArea;
  buildArea(params: { areaType: 'HIGHWAY'; creationParams: HighwayAreaParameters }): HighwayArea;
  buildArea(params: { areaType: 'MANUAL'; creationParams: ManualAreaParameters }): ManualArea;

  buildArea(buildAreaParams: buildAreaParams) {
    const { areaType, creationParams } = buildAreaParams;
    switch (areaType) {
      case 'STATIONARY':
        return this.buildStationaryArea(creationParams);
      case 'RACKING':
        return this.buildRackingArea(creationParams);
      case 'SINGULAR':
        return this.buildSingularStationArea(creationParams);
      case 'HIGHWAY':
        return this.buildHighwayArea(creationParams);
      case 'MANUAL':
        return this.buildManualArea(creationParams);
      default:
        assertNever(areaType);
    }
  }

  private buildStationaryArea(params: StationaryAreaCreationParameters): StationaryArea {
    const { getPreference } = useFloorplanDesignStore.getState();
    const { id, name, rectangle, stationaryType, supportedVehicleId } = params;
    const poiSpec = this.buildStationaryPoiSpec(name, supportedVehicleId);
    return {
      id,
      name,
      areaType: 'STATIONARY',
      rectangle,
      cutOutIds: [],
      stationaryType,
      stationaryPoiSpecs: [poiSpec],
      constellationSpec: {
        stacking: getPreference('stationaryArea.constellationSpec.stacking'),
        angle: getPreference('stationaryArea.constellationSpec.angle'),
        spacing: {
          minMargin: getPreference('stationaryArea.constellationSpec.spacing.minMargin'),
          minGap: getPreference('stationaryArea.constellationSpec.spacing.minGap'),
        },
        distribution: getPreference('stationaryArea.constellationSpec.distribution'),
      },
      connectNeighbors: getPreference('stationaryArea.connectNeighbors'),
      parkingExtension:
        stationaryType === 'PARKING'
          ? {
              priority: getPreference('stationaryArea.parkingExtension.priority'),
            }
          : undefined,
      chargingExtension:
        stationaryType === 'CHARGING'
          ? {
              priority: getPreference('stationaryArea.chargingExtension.priority'),
            }
          : undefined,
      insertionExtension:
        stationaryType === 'INSERTION'
          ? {
              waitDurationInSimulation: getPreference(
                'stationaryArea.insertionExtension.waitDurationInSimulation',
              ),
            }
          : undefined,
      extractionExtension:
        stationaryType === 'EXTRACTION'
          ? {
              waitDurationInSimulation: getPreference(
                'stationaryArea.extractionExtension.waitDurationInSimulation',
              ),
            }
          : undefined,
    };
  }

  private buildRackingArea(params: RackingAreaCreationParameters): RackingArea {
    const { getPreference } = useFloorplanDesignStore.getState();
    const {
      id,
      name,
      rackingLoadHandlingType,
      rectangle,
      supportedLoadCarrierId,
      supportedVehicleId,
    } = params;
    const rackSpecDefault = {
      ...getRackSpecDefault(),
    };

    const canBeUsedForParking = getPreference('rackingArea.canBeUsedForParking');

    return {
      id,
      name,
      areaType: 'RACKING',
      rectangle,
      cutOutIds: [],
      rackType: getPreference('rackingArea.rackType'),
      rackingLoadHandlingType:
        rackingLoadHandlingType ?? getPreference('rackingArea.rackingLoadHandlingType'),
      rackSpec: {
        ...rackSpecDefault,
        loadTypes: [
          {
            loadTypeId: supportedLoadCarrierId,
            stationRegex: undefined,
          },
        ],
      },
      stacking: getPreference('rackingArea.stacking'),
      stationIdGenerator: this.buildRackingStationIdGenerator(
        name,
        getPreference('rackingArea.rackType'),
        rackSpecDefault,
      ),
      poiIdGenerator: this.buildRackingPoiIdGenerator(name, rackSpecDefault),
      vehicleTypeId: supportedVehicleId,
      margin: getPreference('rackingArea.margin'),
      offsetDriving: getPreference('rackingArea.offsetDriving'),
      connectNeighbors: resolveRackingConnectNeighbors(
        getPreference('rackingArea.rackType'),
        getPreference('rackingArea.connectNeighbors'),
      ),
      generateStorageChannel: true,
      insideExtension: this.buildRackInsideExtension(getPreference('rackingArea.rackType')),
      alongsideExtension: this.buildRackAlongsideExtension(getPreference('rackingArea.rackType')),
      shuttleExtension: undefined,
      crabSetting: getPreference('rackingArea.crabSetting'),
      curvature: getPreference('rackingArea.curvature'),
      canBeUsedForParking,
      parkingExtension: canBeUsedForParking
        ? {
            priority: getPreference('rackingArea.parkingExtension.priority'),
          }
        : undefined,
    };
  }

  private buildSingularStationArea(params: SingularStationAreaParameters): SingularStationArea {
    const {
      id,
      name,
      loadHandlingType,
      rectangle,
      supportedLoadCarrierId,
      supportedVehicleId,
      loadCarrierDimensions,
    } = params;
    const { getPreference } = useFloorplanDesignStore.getState();

    return {
      id,
      name,
      areaType: 'SINGULAR',
      rectangle,
      cutOutIds: [],
      loadHandlingType,
      stationSpec: {
        idGenerator: this.buildSingularStationSpecIdGenerator(name),
        z: getPreference('singularStationArea.stationSpec.z'),
        length: loadCarrierDimensions.length,
        width: loadCarrierDimensions.width,
        height: 1.0,
        loadTypeIds: [supportedLoadCarrierId],
        loadHandlingDurationStrategyInSimulation: getPreference(
          'singularStationArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        ),
        loadDurationOverrideInSimulation: getPreference(
          'singularStationArea.stationSpec.loadDurationOverrideInSimulation',
        ),
        unloadDurationOverrideInSimulation: getPreference(
          'singularStationArea.stationSpec.unloadDurationOverrideInSimulation',
        ),
        processingExtension: this.buildProcessingExtension(loadHandlingType),
      },
      loadHandlingPoiSpecs: [
        {
          ...this.buildLoadHandlingPoiSpecBase(name, loadHandlingType, supportedVehicleId),
          angle: getPreference('singularStationArea.loadHandlingPoiSpecs.angle'),
          deltaX: getPreference('singularStationArea.loadHandlingPoiSpecs.deltaX'),
          deltaY: getPreference('singularStationArea.loadHandlingPoiSpecs.deltaY'),
          alwaysFitStation: getPreference(
            'singularStationArea.loadHandlingPoiSpecs.alwaysFitStation',
          ),
        },
      ],
      constellationSpec: {
        stacking: getPreference('singularStationArea.constellationSpec.stacking'),
        angle: getPreference('singularStationArea.constellationSpec.angle'),
        spacing: {
          minMargin: getPreference('singularStationArea.constellationSpec.spacing.minMargin'),
          minGap: getPreference('singularStationArea.constellationSpec.spacing.minGap'),
        },
        distribution: getPreference('singularStationArea.constellationSpec.distribution'),
      },
      connectNeighbors: getPreference('singularStationArea.connectNeighbors'),
    };
  }

  private buildHighwayArea(params: HighwayAreaParameters): HighwayArea {
    const { id, name, supportedVehicleId, segments } = params;

    const { getPreference } = useFloorplanDesignStore.getState();
    const poiSpec = this.buildHighwayAreaPoiSpec(name, supportedVehicleId);
    return {
      id,
      name,
      areaType: 'HIGHWAY',
      cutOutIds: [],
      highwayPoiSpecs: [poiSpec],
      connectUturns: this.getConnectUTurn(poiSpec.roadType),
      connectNeighbors: getPreference('highwayArea.connectNeighbors'),
      segments,
      vehicleLimit: getPreference('highwayArea.vehicleLimit'),
    };
  }

  private buildManualArea(params: ManualAreaParameters): ManualArea {
    const {
      id,
      name,
      rectangle,
      supportedVehicleId,
      supportedLoadCarrierId,
      loadCarrierDimensions,
    } = params;
    const { getPreference } = useFloorplanDesignStore.getState();

    return {
      id,
      name,
      areaType: 'MANUAL',
      vehicleTypeIds: [supportedVehicleId],
      rectangle,
      loadHandlingType: getPreference('manualArea.loadHandlingType'),
      stationSpec: {
        z: getPreference('manualArea.stationSpec.z'),
        loadTypeIds: [supportedLoadCarrierId],
        loadHandlingDurationStrategyInSimulation: getPreference(
          'manualArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        ),
        loadDurationOverrideInSimulation: getPreference(
          'manualArea.stationSpec.loadDurationOverrideInSimulation',
        ),
        unloadDurationOverrideInSimulation: getPreference(
          'manualArea.stationSpec.unloadDurationOverrideInSimulation',
        ),
        idGenerator: this.buildManualStationSpecIdGenerator(name),
        length: loadCarrierDimensions.length,
        width: loadCarrierDimensions.width,
        height: 1.0,
      },
    };
  }

  public buildRackInsideExtension = (
    rackType: RackingArea['rackType'],
  ): RackInsideExtension | undefined => {
    const { getPreference } = useFloorplanDesignStore.getState();

    switch (rackType) {
      case 'ONE_SIDED_INSIDE':
        return {
          forwardParking: getPreference('rackingArea.insideExtension.forwardParking'),
        };
      case 'ONE_SIDED_ALONGSIDE':
      case 'TWO_SIDED_ALONGSIDE':
      case 'SHUTTLE':
        return undefined;
      default:
        assertNever(rackType);
    }
  };

  public buildRackAlongsideExtension = (
    rackType: RackingArea['rackType'],
  ): RackAlongsideExtension | undefined => {
    const { getPreference } = useFloorplanDesignStore.getState();

    switch (rackType) {
      case 'ONE_SIDED_ALONGSIDE':
      case 'TWO_SIDED_ALONGSIDE':
        return {
          aisleWidth: getPreference('rackingArea.alongsideExtension.aisleWidth'),
          poiDirection: getPreference('rackingArea.alongsideExtension.poiDirection'),
        };
      case 'ONE_SIDED_INSIDE':
      case 'SHUTTLE':
        return undefined;
      default:
        assertNever(rackType);
    }
  };

  /**
   * @description returns a processingExtension (or undefined if not applicable) based on the provided loadHandlingType
   */
  private buildProcessingExtension = (
    loadHandlingType: SingularStationArea['loadHandlingType'],
  ): SingularStationArea['stationSpec']['processingExtension'] => {
    const { getPreference } = useFloorplanDesignStore.getState();

    switch (loadHandlingType) {
      case 'INTAKE':
      case 'DELIVERY':
      case 'STORAGE':
      case 'INTAKE_AND_DELIVERY':
        return undefined;
      case 'OFF_VEHICLE_PROCESSING':
      case 'ON_VEHICLE_PROCESSING':
        return {
          processingDurationInSimulation: getPreference(
            'singularStationArea.stationSpec.processingExtension.processingDurationInSimulation',
          ),
        };
      default:
        assertNever(loadHandlingType);
    }
  };

  private getConnectUTurn(roadType: HighwayPoiSpec['roadType']): HighwayArea['connectUturns'] {
    switch (roadType) {
      case 'ONE_WAY':
        return 'NO_UTURN_MAPPING';
      case 'TWO_WAY_INDIA_STYLE':
      case 'TWO_WAY_LEFT_HAND_TRAFFIC':
      case 'TWO_WAY_RIGHT_HAND_TRAFFIC':
        return 'MAP_UTURNS_WITHOUT_PIVOTS';
      default:
        assertNever(roadType);
    }
  }

  public buildHighwayAreaPoiSpec(areaId: string, vehicleTypeId: string): HighwayPoiSpec {
    const highwayPoiSpecDefault = getHighwayPoiSpecDefault();

    return {
      ...highwayPoiSpecDefault,
      vehicleTypeId,
      idGenerator: this.buildHighwayAreaPoiSpecIdGenerator(areaId, vehicleTypeId),
    };
  }

  public buildStationaryPoiSpec(
    areaName: string,
    vehicleTypeId: string,
  ): StationaryArea['stationaryPoiSpecs'][number] {
    return {
      ...POI_SPEC_DEFAULT(),
      vehicleTypeId,
      idGenerator: this.buildStationaryPoiSpecIdGenerator(areaName, vehicleTypeId),
    };
  }

  public buildLoadHandlingPoiSpecBase(
    areaName: string,
    loadHandlingType: SingularStationArea['loadHandlingType'],
    vehicleTypeId: string,
  ): SingularStationArea['loadHandlingPoiSpecs'][number] {
    const defaultPoiSpec = LOAD_HANDLING_POI_SPEC_DEFAULT();

    return {
      ...defaultPoiSpec,
      vehicleTypeId,
      canBeUsedForParking:
        loadHandlingType === 'INTAKE' ? defaultPoiSpec.canBeUsedForParking : false,
      parkingExtension: loadHandlingType === 'INTAKE' ? defaultPoiSpec.parkingExtension : undefined,
      idGenerator: this.buildLoadHandlingPoiSpecIdGenerator(areaName, vehicleTypeId),
    };
  }

  private buildStationaryPoiSpecIdGenerator(
    areaName: string,
    vehicleTypeId: string,
  ): StationaryArea['stationaryPoiSpecs'][number]['idGenerator'] {
    return {
      ...POI_SPEC_DEFAULT().idGenerator,
      format: `${vehicleTypeId}-${areaName}%02d`,
    };
  }

  private buildLoadHandlingPoiSpecIdGenerator(
    areaName: string,
    vehicleTypeId: string,
  ): SingularStationArea['loadHandlingPoiSpecs'][number]['idGenerator'] {
    return {
      ...LOAD_HANDLING_POI_SPEC_DEFAULT().idGenerator,
      format: `${vehicleTypeId}-${areaName} %02d`,
    };
  }

  private buildHighwayAreaPoiSpecIdGenerator(
    areaId: string,
    vehicleTypeId: string,
  ): HighwayPoiSpec['idGenerator'] {
    return {
      ...ID_GENERATOR_DEFAULT,
      format: `${vehicleTypeId}-${areaId}%04d`,
    };
  }

  private buildManualStationSpecIdGenerator(
    name: string,
  ): ManualArea['stationSpec']['idGenerator'] {
    return {
      ...ID_GENERATOR_DEFAULT,
      format: `${name}%02d`,
    };
  }

  private buildSingularStationSpecIdGenerator(
    name: string,
  ): SingularStationArea['stationSpec']['idGenerator'] {
    return {
      ...ID_GENERATOR_DEFAULT,
      format: `${name} %02d`,
    };
  }

  public buildTwoSidedRackingStationIdGenerator(areaName: string, rackSpec: RackSpec): IdGenerator {
    const C = rackSpec.numColumnsInBay > 99 ? 'C%03d' : 'C%02d';
    const S = rackSpec.shelfHeights.length > 99 ? 'S%03d' : 'S%02d';
    const D = rackSpec.numStationsInDepth > 99 ? 'D%03d' : 'D%02d';
    const format = `${areaName}S%02dB%02d${C}${S}${D}`;

    const additionalIncrementers = [
      { startId: 1, increment: 1, numIds: 99999999 }, // the numIds should be null, but not allowed in FDDM atm. Set to a very high number to mimic infinite ids
      { startId: 1, increment: 1, numIds: rackSpec.numColumnsInBay },
      { startId: 1, increment: 1, numIds: rackSpec.shelfHeights.length },
      { startId: 1, increment: 1, numIds: rackSpec.numStationsInDepth },
    ];

    return {
      ...ID_GENERATOR_DEFAULT,
      format,
      additionalIncrementers,
    };
  }

  public buildOneSidedRackingStationIdGenerator(areaName: string, rackSpec: RackSpec): IdGenerator {
    const C = rackSpec.numColumnsInBay > 99 ? 'C%03d' : 'C%02d';
    const S = rackSpec.shelfHeights.length > 99 ? 'S%03d' : 'S%02d';
    const D = rackSpec.numStationsInDepth > 99 ? 'D%03d' : 'D%02d';
    const format = `${areaName}B%02d${C}${S}${D}`;

    const additionalIncrementers = [
      { startId: 1, increment: 1, numIds: rackSpec.numColumnsInBay },
      { startId: 1, increment: 1, numIds: rackSpec.shelfHeights.length },
      { startId: 1, increment: 1, numIds: rackSpec.numStationsInDepth },
    ];

    return {
      ...ID_GENERATOR_DEFAULT,
      format,
      additionalIncrementers,
    };
  }

  private buildRackingStationIdGenerator(
    name: string,
    rackType: RackingArea['rackType'],
    rackSpec: RackingArea['rackSpec'],
  ): RackingArea['stationIdGenerator'] {
    switch (rackType) {
      case 'ONE_SIDED_INSIDE':
      case 'ONE_SIDED_ALONGSIDE':
      case 'SHUTTLE':
        return this.buildOneSidedRackingStationIdGenerator(name, rackSpec);

      case 'TWO_SIDED_ALONGSIDE':
        return this.buildTwoSidedRackingStationIdGenerator(name, rackSpec);

      default:
        assertNever(rackType);
    }
  }

  private buildRackingPoiIdGenerator(areaName: string, rackSpec: RackSpec): IdGenerator {
    const C = rackSpec.numColumnsInBay > 99 ? 'C%03d' : 'C%02d';
    const format = `${areaName}B%02d${C}`;

    const additionalIncrementers = [{ startId: 1, increment: 1, numIds: rackSpec.numColumnsInBay }];
    return {
      ...ID_GENERATOR_DEFAULT,
      format,
      additionalIncrementers,
    };
  }

  public copyArea<T extends Area>(
    area: T,
    overrides: Partial<T> & { name: T['name']; id: T['id'] },
  ): T {
    switch (area.areaType) {
      case 'HIGHWAY': {
        const duplicate: T = {
          ...area,
          highwayPoiSpecs: minItemsArrayUtils.mapAtLeast1(area.highwayPoiSpecs, (item) => ({
            ...item,
            idGenerator: this.buildHighwayAreaPoiSpecIdGenerator(
              overrides.name,
              item.vehicleTypeId,
            ),
          })),
          ...overrides,
        };

        return duplicate;
      }

      case 'MANUAL': {
        const duplicate: T = {
          ...area,
          stationSpec: {
            ...area.stationSpec,
            idGenerator: this.buildManualStationSpecIdGenerator(overrides.name),
            ...(overrides as Partial<ManualArea>).stationSpec,
          },
          ...overrides,
        };

        return duplicate;
      }

      case 'RACKING': {
        const { rackType: rackTypeOverride, rackSpec: rackSpecOverride } = overrides as Partial<
          Extract<Area, RackingArea>
        >;
        const duplicate: Extract<Area, RackingArea> = {
          ...area,
          stationIdGenerator: this.buildRackingStationIdGenerator(
            overrides.name,
            rackTypeOverride || area.rackType,
            rackSpecOverride || area.rackSpec,
          ),
          poiIdGenerator: this.buildRackingPoiIdGenerator(overrides.name, area.rackSpec),
          ...overrides,
        };

        return duplicate as T;
      }

      case 'STATIONARY': {
        const duplicate: T = {
          ...area,
          stationaryPoiSpecs: minItemsArrayUtils.mapAtLeast1(area.stationaryPoiSpecs, (item) => ({
            ...item,
            idGenerator: this.buildStationaryPoiSpecIdGenerator(overrides.id, item.vehicleTypeId),
          })),
          ...overrides,
        };

        return duplicate;
      }

      case 'SINGULAR': {
        const duplicate: T = {
          ...area,
          stationSpec: {
            ...area.stationSpec,
            idGenerator: this.buildSingularStationSpecIdGenerator(overrides.name),
            ...(overrides as Partial<SingularStationArea>).stationSpec,
          },
          loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map((item) => ({
            ...item,
            idGenerator: this.buildLoadHandlingPoiSpecIdGenerator(
              overrides.name,
              item.vehicleTypeId,
            ),
          })),
          ...overrides,
        };

        return duplicate;
      }

      default:
        assertNever(area);
    }
  }
}

export const factory = new Factory();
